/**
 * Support ticket service — creates tickets, fetches them, responds, and sends notifications.
 */
import { supabase } from '../lib/supabase';

/**
 * Generate a unique ticket number: TIK-YYYYMMDD-HHmm-XX
 * Where XX = last 2 digits of user ID hash
 */
export function generateTicketNumber(userId) {
  const now = new Date();
  const datePart = now.toISOString().slice(0, 10).replace(/-/g, '');
  const timePart = String(now.getHours()).padStart(2, '0') + String(now.getMinutes()).padStart(2, '0');
  // Get last 2 chars of userId for uniqueness
  const userSuffix = (userId || 'XX').slice(-2).toUpperCase();
  const random = String(Math.floor(Math.random() * 100)).padStart(2, '0');
  return `TIK-${datePart}-${timePart}-${userSuffix}${random}`;
}

/**
 * Create a new support ticket.
 */
export async function createTicket({ userId, title, description }) {
  if (!supabase) throw new Error('Supabase not configured');
  const ticketNumber = generateTicketNumber(userId);
  const { data, error } = await supabase
    .from('support_tickets')
    .insert({
      ticket_number: ticketNumber,
      user_id: userId,
      title,
      description,
      status: 'open',
      priority: 'normal',
    })
    .select()
    .single();
  if (error) throw error;
  return data;
}

/**
 * Fetch tickets for a specific user.
 */
export async function fetchMyTickets(userId) {
  if (!supabase) return [];
  const { data, error } = await supabase
    .from('support_tickets')
    .select('*')
    .eq('user_id', userId)
    .order('created_at', { ascending: false });
  if (error) { console.error('[tickets]', error.message); return []; }
  return data || [];
}

/**
 * Fetch ALL tickets (for admins).
 */
export async function fetchAllTickets() {
  if (!supabase) return [];
  const { data, error } = await supabase
    .from('support_tickets')
    .select('*, profiles(full_name, email)')
    .order('created_at', { ascending: false });
  if (error) { console.error('[tickets]', error.message); return []; }
  return data || [];
}

/**
 * Update ticket status.
 */
export async function updateTicketStatus(ticketId, status) {
  if (!supabase) throw new Error('Supabase not configured');
  const { data, error } = await supabase
    .from('support_tickets')
    .update({ status })
    .eq('id', ticketId)
    .select()
    .single();
  if (error) throw error;
  return data;
}

/**
 * Add a response to a ticket.
 */
export async function addTicketResponse({ ticketId, responderId, message, isAdmin = false }) {
  if (!supabase) throw new Error('Supabase not configured');
  const { data, error } = await supabase
    .from('ticket_responses')
    .insert({ ticket_id: ticketId, responder_id: responderId, message, is_admin: isAdmin })
    .select()
    .single();
  if (error) throw error;
  return data;
}

/**
 * Fetch responses for a ticket.
 */
export async function fetchTicketResponses(ticketId) {
  if (!supabase) return [];
  const { data, error } = await supabase
    .from('ticket_responses')
    .select('*, profiles(full_name)')
    .eq('ticket_id', ticketId)
    .order('created_at', { ascending: true });
  if (error) { console.error('[responses]', error.message); return []; }
  return data || [];
}

/**
 * Create a notification for a user.
 */
export async function createNotification({ userId, title, message, type = 'info', link }) {
  if (!supabase) return null;
  const { data, error } = await supabase
    .from('notifications')
    .insert({ user_id: userId, title, message, type, link })
    .select()
    .single();
  if (error) { console.error('[notification]', error.message); return null; }
  return data;
}

/**
 * Fetch unread notifications for a user.
 */
export async function fetchNotifications(userId) {
  if (!supabase) return [];
  const { data, error } = await supabase
    .from('notifications')
    .select('*')
    .eq('user_id', userId)
    .order('created_at', { ascending: false })
    .limit(50);
  if (error) { console.error('[notifications]', error.message); return []; }
  return data || [];
}

/**
 * Mark a notification as read.
 */
export async function markNotificationRead(notificationId) {
  if (!supabase) return;
  await supabase
    .from('notifications')
    .update({ read: true })
    .eq('id', notificationId);
}

/**
 * Mark all as read.
 */
export async function markAllNotificationsRead(userId) {
  if (!supabase) return;
  await supabase
    .from('notifications')
    .update({ read: true })
    .eq('user_id', userId)
    .eq('read', false);
}
